import { Map } from 'immutable'
import { MeasurementMetadata } from './Metadata';

/**
 * Represents a basic measurement.
 */
export type Measurement = {
    /**
     * The key of the measurement.
     */
    key: MeasurementKey;

    /**
     * The type of the measurement.
     */
    type: MeasurementType;
}

/**
 * Represents all the possible types of measurements.
 */
export type MeasurementType = "line" | "derived" | "unmeasurable" | "single-value" | "categoric";

/**
 * Represents a measurement key.
 */
export type MeasurementKey = string;

// MARK: - Primary Measurements

/**
 * Represents a line measurement.
 */
export type LineMeasurement = Measurement & {
    type: "line";

    /**
     * The direction of the line.
     */
    direction: LineDirection;

    /**
     * The start point of the line.
     */
    startPosition: Point;

    /**
     * The end point of the line.
     */
    endPosition: Point;

    /**
     * Whether this measurement is drawable.
     */
    drawable: true;
}

export type LineDirection = 'horizontal' | 'vertical' | 'free';

/**
 * Whether the measurement is a line measurement.
 * @param measurement The measurement to check.
 * @returns True if the measurement is a line measurement.
 */
export function isLineMeasurement(measurement: Measurement): measurement is LineMeasurement {
    return measurement.type === "line";
}

/**
 * Represents a single value measurement.
 */
export type SingleValueMeasurement = Measurement & {
    type: "single-value";

    /**
     * The value of the measurement.
     */
    value: number;

    /**
     * Whether this measurement is drawable.
     */
    drawable: false;
}

export function isSingleValueMeasurement(measurement: Measurement): measurement is SingleValueMeasurement {
    return measurement.type === "single-value";
}

export type CategoricMeasurement = Measurement & {
    type: "categoric";

    /**
     * The value of the measurement.
     */
    value: string;

    /**
     * Whether this measurement is drawable.
     */
    drawable: false;
};

export function isCategoricMeasurement(measurement: Measurement): measurement is CategoricMeasurement {
    return measurement.type === "categoric";
}

// MARK: - Input Measurements

/**
 * An input measurement is the union type of all measurement types that can be input by the user.
 */
export type InputMeasurement = LineMeasurement | SingleValueMeasurement | UnmeasurableMeasurement | CategoricMeasurement;
export type InputMeasurementCollection = Map<MeasurementKey, InputMeasurement>;


// MARK: - Unmeasurable Measurement

/**
 * Represents an unmeasurable measurement.
 */
export type UnmeasurableMeasurement = Measurement & {
    type: "unmeasurable";

    /**
     * Whether this measurement is drawable.
     */
    drawable: false;
}

/**
 * Whether the measurement is an unmeasurable measurement.
 * @param measurement The measurement to check.
 * @returns True if the measurement is an unmeasurable measurement.
 */
export function isUnmeasurableMeasurement(measurement: Measurement): measurement is UnmeasurableMeasurement {
    return measurement.type === "unmeasurable";
}

// MARK: - Derived Measurements

/**
 * Represents a derived measurement.
 */
export type DerivedMeasurement = Measurement & {
    type: "derived";

    /**
     * The metadata of the derived measurement.
     */
    metadata: MeasurementMetadata;

    /**
     * The measurements that this derived measurement is derived from.
     */
    dependencies: MeasurementKey[];

    /**
     * The default value of the derived measurement.
     */
    default: number | undefined;

    /**
     * The function that calculates the derived measurement.
     */
    calculate: (values: Map<MeasurementKey, number>) => number;
}

/**
 * Whether the measurement is a derived measurement.
 * @param measurement The measurement to check.
 * @returns True if the measurement is a derived measurement.
 */
export function isDerivedMeasurement(measurement: Measurement): measurement is DerivedMeasurement {
    return measurement.type === "derived";
}

export type DerivedMeasurementCollection = Map<MeasurementKey, DerivedMeasurement>;

// MARK: - Measurement Result

type BaseMeasurementResult = {
    /**
     * The key of the result measurement. Will match the key of the input measurement.
     */
    key: MeasurementKey

    /**
     * The type of the measurement this result is for.
     */
    measurementType: MeasurementType

    /**
     * The type of the result measurement.
     */
    type: "numeric" | "failure";
}

export type NumericMeasurementResult = BaseMeasurementResult & {
    type: "numeric";

    /**
     * The value of the result measurement.
     */
    value: number;
}

export type FailureMeasurementResult = BaseMeasurementResult & {
    type: "failure";

    /**
     * The error message.
     */
    error: string;
}

export function isNumericMeasurementResult(result: MeasurementResult): result is NumericMeasurementResult {
    return result.type === "numeric";
}

export function isFailureMeasurementResult(result: MeasurementResult): result is FailureMeasurementResult {
    return result.type === "failure";
}

export type MeasurementResult = NumericMeasurementResult | FailureMeasurementResult;

// MARK: - Measurement requirement. 

/// A required measurement simply annotates a measurement as required. It has no additional properties.
type BaseMeasurementRequirement = {
    key: MeasurementKey;
    type: MeasurementType;

    /**
     * The metadata of the required measurement.
     */
    metadata: MeasurementMetadata;

    /**
     * A function that determines whether the requirement should be skipped based on the current state of the task. Optional.
     */
    shouldSkipRequirement?: (state: { inputs: InputMeasurementCollection, results: MeasurementResult[] }) => boolean;
}

/**
 * Represents a single value measurement requirement.
 */
export type SingleValueMeasurementRequirement = BaseMeasurementRequirement & {
    type: "single-value";
}

export function isSingleValueMeasurementRequirement(requirement: MeasurementRequirement): requirement is SingleValueMeasurementRequirement {
    return requirement.type === 'single-value';
}

/**
 * Represents a line measurement requirement.
 */
export type LineMeasurementRequirement = BaseMeasurementRequirement & {
    type: "line";
    direction: LineDirection;
}

export function isLineMeasurementRequirement(requirement: MeasurementRequirement): requirement is LineMeasurementRequirement {
    return requirement.type === 'line';
}

/**
 * Represents a categoric measurement requirement with a set of options.
 */
export type CategoricMeasurementRequirement = BaseMeasurementRequirement & {
    type: "categoric";
    options: string[];
}

export function isCategoricMeasurementRequirement(requirement: MeasurementRequirement): requirement is CategoricMeasurementRequirement {
    return requirement.type === 'categoric';
}

export type MeasurementRequirement = SingleValueMeasurementRequirement | LineMeasurementRequirement | CategoricMeasurementRequirement;

// MARK: - Helper Types

/**
 * Represents a point in space.
 */
export type Point = {
    /**
     * The x coordinate of the point.
     */
    x: number;

    /**
     * The y coordinate of the point.
     */
    y: number;
}

/**
 * A collection of measurements.
 */
export type MeasurementCollection = Map<MeasurementKey, Measurement>;

export function joinMeasurementCollections(...collections: MeasurementCollection[]): MeasurementCollection {
    let result = Map<MeasurementKey, Measurement>();

    for(const collection of collections) {
        result = result.merge(collection);
    }

    return result;
}